import { computed, inject, Injectable, signal } from "@angular/core";

import {
  RoleAccessData,
  RoleClaim,
  ScopeValue,
} from "@bitwarden/web-vault/app/models/data/role-access.data";
import { StateService } from "@bitwarden/web-vault/app/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { LogService } from "@bitwarden/common/abstractions/log.service";

@Injectable({
  providedIn: "root",
})
export class RoleAccessService {
  protected log: LogService = inject(LogService);
  private stateService = inject(StateService);
  private roleAccess: RoleAccessData = new RoleAccessData();
  private roleAccessVersion = signal(0);

  /**
   * Reactive counterpart to {@link isBetaUser}, for use inside `computed()`/templates.
   * `isBetaUser()` reads plain (non-signal) state, so it won't invalidate a computed
   * when role data loads after the computed's first evaluation.
   */
  isBetaUserSignal = computed(() => {
    this.roleAccessVersion();
    return this.isBetaUser();
  });

  constructor() {
    this.stateService.activeAccount$
      .pipe(takeUntilDestroyed())
      .subscribe(this.initialise.bind(this));
  }

  async initialise(userId: string) {
    this.log.info(`Session Open for user ${userId}`);
    const roleScope = await this.stateService.getRoleScope();
    const roleClaim = (await this.stateService.getRoleClaim()) as RoleClaim;
    // Called unconditionally so an empty resolution still resets: state.service#getRoleScope()
    // falls back to "" and #getRoleClaim() to {} (they never resolve undefined), "" splits to
    // [""] which grants no scope, and setClaims() merges over RoleAccessData's defaults rather
    // than over the previous claim - so a never-populated state lands on safe defaults.
    // roleScope/roleClaim live in *global* state, not per-account, so what this reads is whatever
    // the last login/token refresh wrote. #4981: logout clears the stored copy
    // (clearStoredRoleAccess), so a later login whose token omits them no longer leaves the
    // previous account's values for this to re-apply, and logIn.strategy writes the response's
    // values *before* addAccount() so this read (triggered by its activeAccount$ emission) sees them.
    this.setRoleScope(roleScope);
    this.setRoleClaim(roleClaim);
  }

  setRoleScope(roleScope: string) {
    const scope = (roleScope?.split(" ") ?? []) as ScopeValue[];
    this.roleAccess.setScope(scope);
    this.roleAccessVersion.update((v) => v + 1);
  }

  setRoleClaim(roleClaims: RoleClaim) {
    this.roleAccess.setClaims(roleClaims);
    this.roleAccessVersion.update((v) => v + 1);
  }

  getRoleAccess() {
    return this.roleAccess;
  }

  isBetaUser() {
    this.roleAccess = this.getRoleAccess();
    const scope = this.roleAccess.getScope();
    return scope?.includes("BETA_ACCESS") ?? false;
  }

  clearRoleAccess(): void {
    this.roleAccess.setScope([]);
    this.roleAccess.clearClaims();
    this.roleAccessVersion.update((v) => v + 1);
  }

  /**
   * Clears the *persisted* roleScope/roleClaim (global state, localStorage). clearRoleAccess()
   * above only resets the in-memory copy, so on its own the next login would start from the
   * previous account's stored values (#4981). Call on logout only - never on token refresh, which
   * must keep the session's entitlements.
   */
  async clearStoredRoleAccess(): Promise<void> {
    await this.stateService.setRoleScope("");
    await this.stateService.setRoleClaim({});
  }

  /**
   * Logout: the persisted role data first, then the in-memory copy. Order matters - stateService
   * .clean() re-emits activeAccount$, which re-runs initialise() and refills memory from storage,
   * so resetting memory before the storage write lands would let that read win. Memory is reset even
   * if the storage write fails (the error still propagates).
   */
  async clearForLogout(): Promise<void> {
    try {
      await this.clearStoredRoleAccess();
    } finally {
      this.clearRoleAccess();
    }
  }
}
